import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Search,
  MapPin,
  Star,
  Shield,
  Loader2,
  Building2,
  ArrowRight,
  Info,
  X,
} from 'lucide-react';
import { publicJobsService } from '../../services/publicJobsService';
import { apiService } from '../../services/apiService';
import { useGeolocation, SACRAMENTO_FALLBACK } from '../../hooks/useGeolocation';

// Flatten the hierarchical `/api/v1/public/services` payload into a single
// list of { id, name, label } we can autocomplete against. Includes both
// top-level categories ("Plumbing") and leaf services ("Boiler repair").
const flattenServiceCatalog = (catalog) => {
  if (!Array.isArray(catalog)) return [];
  const out = [];
  catalog.forEach((cat) => {
    if (!cat?.name) return;
    out.push({ id: `cat-${cat.id}`, name: cat.name, label: cat.name, isCategory: true });
    (cat.subcategories || []).forEach((sub) => {
      if (!sub?.name) return;
      out.push({
        id: `svc-${sub.id}`,
        name: sub.name,
        label: sub.full_path || `${cat.name} > ${sub.name}`,
        isCategory: false,
      });
    });
  });
  return out;
};

const ProviderCard = ({ provider }) => {
  const name = provider?.business_name || provider?.name || 'Verified provider';
  const city = provider?.location?.city || provider?.city;
  const state = provider?.location?.state || provider?.state;
  const rating = provider?.average_rating ?? provider?.rating;
  const reviews = provider?.reviews_count ?? provider?.review_count;

  return (
    <Link
      to={`/provider/${provider?.id}`}
      className="block group"
    >
      <Card className="h-full hover:shadow-md transition-shadow border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h4 className="font-semibold text-gray-900 truncate group-hover:text-emerald-700">{name}</h4>
                {provider?.verification_status === 'verified' && (
                  <Shield className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-label="Verified" />
                )}
              </div>
              {(city || state) && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {[city, state].filter(Boolean).join(', ')}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 text-xs">
                {rating != null && (
                  <span className="inline-flex items-center gap-0.5 text-amber-600 font-medium">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    {Number(rating).toFixed(1)}
                  </span>
                )}
                {reviews != null && (
                  <span className="text-gray-500">({reviews} reviews)</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const ProviderSearchSection = () => {
  const { coords, status, request } = useGeolocation({ autoRequest: true });
  const [query, setQuery] = useState('');
  const [postcode, setPostcode] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [defaulted, setDefaulted] = useState(false);

  // Service catalog autocomplete state.
  const [allServices, setAllServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Pull the full hierarchical catalog once and flatten it for autocomplete.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setServicesLoading(true);
      try {
        const data = await apiService.get('/api/v1/public/services', { includeAuth: false });
        if (!cancelled) setAllServices(flattenServiceCatalog(data));
      } catch (e) {
        if (!cancelled) setAllServices([]);
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Filter the catalog against the typed query. Up to 8 results.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allServices.slice(0, 8);
    return allServices
      .filter((s) => s.label.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, allServices]);

  // Close the suggestion popover when clicking outside.
  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Seed postcode from geolocation fallback ONCE so the initial query targets
  // Sacramento. Using a ref instead of depending on `postcode` so that clearing
  // the field by the user doesn't immediately re-fill it from the fallback —
  // that previously made the input feel "uncloseable".
  const hasSeededPostcode = useRef(false);
  useEffect(() => {
    if (hasSeededPostcode.current) return;
    if (status === 'fallback' || status === 'denied' || status === 'unsupported' || status === 'error') {
      hasSeededPostcode.current = true;
      if (!postcode) setPostcode(SACRAMENTO_FALLBACK.postcode);
    }
  }, [status, postcode]);

  const runSearch = useCallback(async (params) => {
    setLoading(true);
    try {
      const res = await publicJobsService.searchProviders({ ...params, perPage: 6 });
      setData(res);
      setDefaulted(Boolean(res?.search_params?.location_defaulted));
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial preview load using fallback/postcode.
  useEffect(() => {
    if (!submitted && postcode) {
      runSearch({ postcode });
    }
  }, [postcode, submitted, runSearch]);

  const submitSearch = useCallback((overrideQuery) => {
    const q = (overrideQuery ?? query).trim();
    setSubmitted(true);
    setShowSuggestions(false);
    runSearch({
      serviceCategory: q || undefined,
      postcode: postcode || undefined,
    });
  }, [query, postcode, runSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitSearch();
  };

  // Keyboard navigation over the suggestion list.
  const handleQueryKeyDown = (e) => {
    if (!showSuggestions && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setShowSuggestions(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      // Pick the highlighted suggestion if one is selected; otherwise submit
      // the form with whatever's typed. Either way, Enter ALWAYS triggers a
      // search — fixes the prior bug where Enter did nothing.
      e.preventDefault();
      if (highlightIdx >= 0 && suggestions[highlightIdx]) {
        const picked = suggestions[highlightIdx].name;
        setQuery(picked);
        submitSearch(picked);
      } else {
        submitSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Same Enter-to-submit behavior on the postcode input — defensive in case
  // the form's onSubmit ever fails to fire.
  const handlePostcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
    }
  };

  const pickSuggestion = (s) => {
    setQuery(s.name);
    setHighlightIdx(-1);
    setShowSuggestions(false);
    submitSearch(s.name);
    inputRef.current?.blur();
  };

  const clearQuery = () => {
    setQuery('');
    setHighlightIdx(-1);
    inputRef.current?.focus();
  };

  const providers = data?.data || [];

  return (
    <section className="py-12 bg-white border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Find providers near you</h2>
            <p className="text-gray-600 mt-1">
              Search verified pros in your area — no sign-in required.
            </p>
          </div>
          <Link
            to="/services"
            className="text-sm font-medium text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          >
            Browse all providers <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {/* Service autocomplete — pulls the full hierarchical catalog from
                /api/v1/public/services. Up/Down arrows + Enter to pick. Enter
                with no highlight still submits whatever text is typed. */}
            <div className="md:col-span-6 relative" ref={wrapperRef}>
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); setHighlightIdx(-1); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleQueryKeyDown}
                placeholder={servicesLoading ? 'Loading services…' : 'What do you need? (e.g. Plumbing)'}
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                className="w-full pl-9 pr-9 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearQuery}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <ul
                  role="listbox"
                  className="absolute z-20 left-0 right-0 mt-1 max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                >
                  {suggestions.map((s, idx) => (
                    <li
                      key={s.id}
                      role="option"
                      aria-selected={idx === highlightIdx}
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                        idx === highlightIdx ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">{s.label}</span>
                      {s.isCategory && (
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400 shrink-0">Category</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="md:col-span-4 relative">
              <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={handlePostcodeKeyDown}
                placeholder="Postcode"
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
                inputMode="numeric"
                maxLength={10}
              />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </div>

          {/* Sacramento-fallback notice. Made into a proper banner so users
              don't think the platform only has one provider — they now know
              they're looking at a default area and can change it. */}
          {defaulted && (
            <div className="mt-3 flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
              <span>
                Showing providers in <strong>Sacramento (95814)</strong> as a default. Enter your postcode above
                {status !== 'requesting' && (
                  <>
                    {' '}or{' '}
                    <button
                      type="button"
                      onClick={request}
                      className="font-medium underline hover:text-emerald-900"
                    >
                      use your current location
                    </button>
                  </>
                )}
                {' '}to see providers near you.
              </span>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-2">
              {(status === 'idle' || status === 'requesting') ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Detecting your location…
                </span>
              ) : (
                <button type="button" onClick={request} className="inline-flex items-center gap-1 hover:text-emerald-700">
                  <MapPin className="w-3 h-3" /> Use my location
                </button>
              )}
            </div>
          </div>
        </form>

        {(providers.length > 0 || loading) && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loading && providers.length === 0 ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}><CardContent className="p-4 animate-pulse"><div className="h-12 bg-gray-100 rounded" /></CardContent></Card>
              ))
            ) : (
              providers.map((p) => <ProviderCard key={p.id} provider={p} />)
            )}
          </div>
        )}

        {submitted && !loading && providers.length === 0 && (
          <Card className="mt-6">
            <CardContent className="p-6 text-center text-gray-600 text-sm">
              No providers matched that search. Try a broader service or a different postcode.
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};

export default ProviderSearchSection;
